/**
 * ArtworkAndProofs — the order's Artwork step (the journeys branch's round 18
 * mock-up; docs/features/order-to-invoice/66_views_and_frontend_artwork_update_spec.md
 * §2): one component in the order's chain, and the quote page's file column
 * reads the same versions.
 *
 * One row per version (a line with one version is one row, with no version
 * label), in the rail's own rows: node · ".01 · E Wales" · the line · "Rev 2
 * · can't print · fonts not embedded" · its one next step · ⋯ · an ⓘ that
 * opens the version's files per part (what was reused, what is shared), the
 * findings per part with staff's check names, its proofs and who decided them,
 * and one history, newest first. A line that needs none says why.
 *
 * Every stage, sentence and URL is the read's: the stage derivation is
 * views' (doc 66 §6.3). A step shows only where the read carries its URL; the
 * page holds the dialog that a step opens (`onOpenDialog`), so the action
 * box's rows open the same ones.
 */
import { useCallback } from "react";
import { Download, MoreHorizontal } from "lucide-react";
import type {
  OrderArtworkView,
  StaffArtworkLine,
  StaffFinding,
  StaffPart,
  StaffProof,
} from "@shared-core/data/artwork-staff-types";
import {
  moreStepsOf,
  nextStepOf,
  stageNode,
  stepLabel,
  type ArtworkStep,
} from "@shared-core/domain/artwork-staff";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { SupplyGuide } from "./SupplyGuide";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../primitives/dropdown-menu";
import { InfoTip } from "../../patterns/InfoTip";
import { RailRow, RailRows } from "../../patterns/LifecycleRail";
import { ObjectChip } from "../../patterns/ObjectChip";
import { PanelHeading, ViewDetail } from "../orders/StepPanelParts";
import {
  targetName,
  type ArtworkDialogRequest,
  type ArtworkTarget,
} from "./ArtworkDialogs";

const short = formatShortDateOrDash;

export interface ArtworkHandlers {
  busy: boolean;
  /** Open the dialog a step needs (the page hosts it). */
  onOpenDialog: (request: ArtworkDialogRequest) => void;
  /**
   * Open the Contact dialog for the customer: a drafted email or a logged
   * call. Absent where there is none: no Contact is offered.
   */
  onContact?: () => void;
  /** Fetch a file or a proof PDF at the URL the read carried and save it. */
  onDownload: (url: string, label: string) => void;
}

/** The dialog a step opens. */
function requestFor(
  step: ArtworkStep,
  versionId: string,
): ArtworkDialogRequest | null {
  switch (step) {
    case "tag_pages":
    case "upload":
    case "upload_new":
      // A version with one file waiting for its tags opens the same dialog on "Which pages are which?".
      return { kind: "upload", versionId, replacing: false };
    case "replace_part":
      return { kind: "upload", versionId, replacing: true };
    case "check_warnings":
      return { kind: "check_warnings", versionId };
    case "accept_without_proof":
      return { kind: "accept", versionId };
    case "decide_for_customer":
      return { kind: "decide_proof", versionId };
    case "resend_proof":
      return { kind: "resend", versionId };
    case "reopen":
      return { kind: "reopen", versionId };
    case "contact":
      return null;
  }
}

export function ArtworkAndProofs({
  artwork,
  lineNames,
  party,
  handlers,
  focusLineNo = null,
  focusVersion = null,
}: {
  artwork: OrderArtworkView;
  /** Line title by line number ("Window posters"). */
  lineNames: ReadonlyMap<string, string>;
  /** The customer: who proofs go to ("with Acme Ltd"). */
  party: string;
  /** Null when the step only reads. */
  handlers: ArtworkHandlers | null;
  /** Opened at a line (`?line=`): its rows are marked and scroll into view. */
  focusLineNo?: string | null;
  /** Opened at one version of that line: only its row is marked. */
  focusVersion?: string | null;
}) {
  // The focused line's row scrolls into view once, as it mounts.
  const focusRef = useCallback((row: HTMLElement | null) => {
    row?.scrollIntoView({ block: "center" });
  }, []);
  const milestone = artwork.milestone;
  const summary = [milestone?.phrase, milestone?.note]
    .filter(Boolean)
    .join(" · ");
  const hasVersions = artwork.lines.some((l) => l.kind === "artwork");

  const rowsOf = (line: StaffArtworkLine) => {
    const lineName = lineNames.get(line.line_no) ?? line.line_no;
    const isFocusedLine = line.line_no === focusLineNo;
    if (line.kind === "none") {
      return [
        <RailRow
          key={line.line_ref}
          rowRef={isFocusedLine ? focusRef : undefined}
          isFocused={isFocusedLine}
          state="to_do"
          problem={null}
          label={line.line_no}
          name={lineName}
          status={line.none_text ?? "No artwork: none needed"}
        />,
      ];
    }
    return line.versions.map((version, index) => {
      const isFocused =
        isFocusedLine &&
        (focusVersion === null || focusVersion === version.label);
      const isFirstFocused =
        isFocused && (focusVersion !== null || index === 0);
      return (
        <VersionRailRow
          key={version.version_id}
          target={{ line, lineName, version }}
          // The line's facts (from the quote, a repeat, released to): once, on its first row.
          chips={index === 0 ? line.chips : []}
          isFocused={isFocused}
          rowRef={isFirstFocused ? focusRef : undefined}
          party={party}
          handlers={handlers}
        />
      );
    });
  };

  return (
    <div className="space-y-3" data-section-id="order.artwork">
      <PanelHeading
        title="Artwork"
        infoLabel="About artwork and proofs"
        info={
          <>
            Each version has a file for every part it needs, checked as it
            arrives. A proof is made once the files are in and checked, and goes
            to the customer, who approves it or sends it back; a file that can
            only be printed with a change we make is corrected first. Decide a
            proof for the customer only when they tell us; it is recorded as
            decided by Pollards for them.
          </>
        }
        summary={
          // A read-only step is as it stood: its counts would mislead.
          summary && handlers ? (
            <span data-testid="artwork-summary">{summary}</span>
          ) : undefined
        }
      />
      {!hasVersions ? (
        <p className="text-sm text-muted-foreground">
          {[
            ...new Set(
              artwork.lines.flatMap((l) => (l.none_text ? [l.none_text] : [])),
            ),
          ].join(". ") || "No artwork: none needed"}
          .
        </p>
      ) : (
        <RailRows ariaLabel="Artwork">{artwork.lines.flatMap(rowsOf)}</RailRows>
      )}
    </div>
  );
}

/** The row's one next step and the rest in its ⋯. */
function VersionSteps({
  target,
  party,
  handlers,
}: {
  target: ArtworkTarget;
  party: string;
  handlers: ArtworkHandlers | null;
}) {
  const { version } = target;
  const next = handlers
    ? nextStepOf(version, handlers.onContact !== undefined)
    : null;
  const more = handlers ? moreStepsOf(version, next) : [];
  const name = targetName(target);
  if (!handlers || (!next && more.length === 0)) return null;
  const run = (step: ArtworkStep) => {
    if (step === "contact") return handlers.onContact?.();
    const request = requestFor(step, version.version_id);
    if (request) handlers.onOpenDialog(request);
  };
  return (
    <>
      {next && (
        <Button
          size="sm"
          variant="outline"
          disabled={handlers.busy}
          aria-label={`${stepLabel(next, version, party)}: ${name}`}
          onClick={() => run(next)}
        >
          {stepLabel(next, version, party)}
        </Button>
      )}
      {more.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 px-0"
              disabled={handlers.busy}
              aria-label={`More for ${name}`}
            >
              <MoreHorizontal aria-hidden className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {more.map((step) => (
              <DropdownMenuItem key={step} onSelect={() => run(step)}>
                {stepLabel(step, version, party)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}

/**
 * A version under the order's rail, as one rail row: ".01 · E Wales" · the
 * line · "Rev 2 · can't print · fonts not embedded" · its step · ⋯ · ⓘ.
 */
function VersionRailRow({
  target,
  chips,
  isFocused,
  rowRef,
  party,
  handlers,
}: {
  target: ArtworkTarget;
  /** "From the quote", "Repeat of ORD-K48193.01", "Released to PDQ": the read's words. */
  chips: string[];
  isFocused: boolean;
  rowRef?: (row: HTMLLIElement | null) => void;
  party: string;
  handlers: ArtworkHandlers | null;
}) {
  const { line, version, lineName } = target;
  const label = version.display
    ? `${line.line_no} · ${version.display}`
    : line.line_no;
  const fromItem = version.from_item;
  return (
    <RailRow
      rowRef={rowRef}
      isFocused={isFocused}
      {...stageNode(version.stage)}
      label={label}
      name={
        chips.length > 0 || fromItem ? (
          <span className="flex flex-wrap items-center gap-1">
            {lineName}
            {chips.map((chip) => (
              <Badge key={chip} variant="neutral">
                {chip}
              </Badge>
            ))}
            {fromItem && (
              // Its files change on the item, not here: the chip opens the item.
              <ObjectChip
                label={fromItem.label}
                to={paths.catalogueItem({ itemId: fromItem.catalogue_item_id })}
              />
            )}
          </span>
        ) : (
          lineName
        )
      }
      spokenName={
        version.display ?? (chips.length > 0 || fromItem ? lineName : undefined)
      }
      status={version.status_text}
      actions={
        <VersionSteps target={target} party={party} handlers={handlers} />
      }
      detail={
        <ViewDetail title={`Artwork of ${targetName(target)}`}>
          <VersionDetail target={target} handlers={handlers} />
        </ViewDetail>
      }
    />
  );
}

const TIER_WORDS: Record<StaffFinding["tier"], string> = {
  cant_print: "Can't print",
  worth_knowing: "Worth knowing",
  we_will_sort: "We'll sort it",
  unrecognised: "Held: not known yet",
};

/** A finding as staff read it: the customer's tier and sentence, then the check and what it found. */
function FindingItem({ finding }: { finding: StaffFinding }) {
  return (
    <li>
      <span className="font-medium">{TIER_WORDS[finding.tier]}:</span>{" "}
      {finding.sentence ??
        "Not worded for the customer: they are told we are taking a closer look."}
      <span className="block text-xs text-muted-foreground">
        {finding.check_name}
        {finding.detail && ` · ${finding.detail}`}
      </span>
    </li>
  );
}

function PartFiles({
  part,
  name,
  isWaitingForTags,
  handlers,
}: {
  part: StaffPart;
  name: string;
  /** One file for every part waits for its page tags: a part with no file waits for them. */
  isWaitingForTags: boolean;
  handlers: ArtworkHandlers | null;
}) {
  const file = part.file;
  return (
    <li className="space-y-1">
      <div className="flex flex-wrap items-center gap-x-2">
        {part.label && (
          <span className="w-12 text-muted-foreground">{part.label}</span>
        )}
        {file ? (
          <>
            <span className="break-all">{file.file_name}</span>
            {file.split_from && (
              <span className="text-xs text-muted-foreground">
                From {file.split_from.file_name}, {file.split_from.pages_label}
              </span>
            )}
            {file.reused_from_revision !== null && (
              <span className="text-xs text-muted-foreground">
                (reused from Rev {file.reused_from_revision})
              </span>
            )}
            {file.shared_with.length > 0 && (
              <span className="text-xs text-muted-foreground">
                shared with {file.shared_with.join(", ")}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {file.channel_label} {short(file.uploaded_at)} by{" "}
              {file.uploaded_by}
            </span>
            {handlers && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                aria-label={`Download original: ${file.file_name}`}
                onClick={() =>
                  handlers.onDownload(file.download_url, file.file_name)
                }
              >
                <Download aria-hidden className="size-3.5" />
                Download original
              </Button>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">
            {isWaitingForTags
              ? "Waiting for the page tags"
              : `No file yet${name ? ` for ${name}` : ""}`}
          </span>
        )}
      </div>
      <SupplyGuide sentence={part.supply_sentence} notes={part.supply_notes} />
      {part.findings.length > 0 && (
        <ul className="list-disc space-y-1 pl-5">
          {part.findings.map((f) => (
            <FindingItem key={f.id} finding={f} />
          ))}
        </ul>
      )}
    </li>
  );
}

function ProofItem({
  proof,
  handlers,
}: {
  proof: StaffProof;
  handlers: ArtworkHandlers | null;
}) {
  return (
    <li className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-x-2">
        <span className="font-medium">{proof.label}</span>
        <span className="text-muted-foreground">
          of Rev {proof.of_revision} · {proof.status_line}
        </span>
        {proof.pdf_url && handlers && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            aria-label={`View ${proof.label}`}
            onClick={() => handlers.onDownload(proof.pdf_url!, proof.label)}
          >
            <Download aria-hidden className="size-3.5" />
            View {proof.label}
          </Button>
        )}
      </div>
      {proof.how_approved && (
        <p className="text-xs text-muted-foreground">
          How approved: {proof.how_approved}
        </p>
      )}
      {proof.reason_label && (
        <p className="text-xs text-muted-foreground">
          {proof.reason_label}
          {proof.comment && `: “${proof.comment}”`}
        </p>
      )}
      {proof.accepted_findings.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Accepted: {proof.accepted_findings.map((f) => f.sentence).join(" ")}
        </p>
      )}
      {proof.reopen_refusal && (
        <p className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          {proof.reopen_refusal}
          <InfoTip icon="lock" label={`Why ${proof.label} can't be reopened`}>
            {proof.reopen_refusal}
          </InfoTip>
        </p>
      )}
    </li>
  );
}

/** A version's files per part (reuse and sharing marked), its proofs and its one history. */
function VersionDetail({
  target,
  handlers,
}: {
  target: ArtworkTarget;
  handlers: ArtworkHandlers | null;
}) {
  const { version, line } = target;
  const name = targetName(target);
  return (
    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
      <div className="space-y-3">
        <section aria-label={`Files of ${name}`}>
          <h3 className="text-xs font-medium text-muted-foreground">
            {version.current_revision
              ? `Rev ${version.current_revision} · files`
              : "Files"}
          </h3>
          <ul className="mt-1 space-y-2 text-sm">
            {version.parts.map((part) => (
              <PartFiles
                key={part.part_key}
                part={part}
                name={version.display ?? line.line_no}
                isWaitingForTags={version.pending_combined_file != null}
                handlers={handlers}
              />
            ))}
          </ul>
          {version.original_files.length > 0 && (
            <ul
              aria-label={`Originals of ${name}`}
              className="mt-2 space-y-1 text-sm"
            >
              {version.original_files.map((original) => (
                <li
                  key={original.download_url}
                  className="flex flex-wrap items-center gap-x-2"
                >
                  <span className="text-muted-foreground">Original</span>
                  <span className="break-all">{original.file_name}</span>
                  {handlers && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2"
                      aria-label={`Download the original: ${original.file_name}`}
                      onClick={() =>
                        handlers.onDownload(
                          original.download_url,
                          original.file_name,
                        )
                      }
                    >
                      <Download aria-hidden className="size-3.5" />
                      Download the original
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {version.combined_file_notes.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
              {version.combined_file_notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}
          {version.colours_unknown && (
            <p className="mt-1 text-xs text-muted-foreground">
              Colours and stock not known: spot colours are kept, not converted.
            </p>
          )}
          {version.from_item && (
            <p className="mt-1 text-xs text-muted-foreground">
              {version.from_item.label}
              {version.from_item.stop_reason &&
                ` · ${version.from_item.stop_reason}`}
            </p>
          )}
        </section>
        {version.proofs.length > 0 && (
          <section aria-label={`Proofs of ${name}`}>
            <h3 className="text-xs font-medium text-muted-foreground">
              Proofs
            </h3>
            <ul className="mt-1 space-y-2 text-sm">
              {version.proofs.map((proof) => (
                <ProofItem key={proof.id} proof={proof} handlers={handlers} />
              ))}
            </ul>
          </section>
        )}
      </div>
      <section aria-label={`History of ${name}`}>
        <h3 className="text-xs font-medium text-muted-foreground">History</h3>
        {version.history.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">Nothing yet.</p>
        ) : (
          <ol className="mt-1 space-y-1 text-sm">
            {version.history.map((event, i) => (
              <li key={`${event.at}-${i}`}>
                <span className="tabular-nums text-muted-foreground">
                  {short(event.at)}
                </span>
                <span className="text-muted-foreground"> · </span>
                {event.text}
                <span className="text-muted-foreground">
                  {event.by && ` · ${event.by}`}
                </span>
                {event.quote_no && (
                  <>
                    {" "}
                    <ObjectChip
                      label={event.quote_no}
                      detail="on the quote"
                      to={paths.quoteDetail({ quoteNo: event.quote_no })}
                    />
                  </>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
